import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ContactGroupMeta,
  GroupContact,
  CreateContactGroupInput,
  UpdateContactGroupInput,
  CreateGroupContactInput,
  UpdateGroupContactInput,
} from '@bom/shared';
import { api } from '../lib/apiClient';

/** 平台级：联系人分组列表（含 contact_count） */
export function useContactGroups(enabled = true) {
  return useQuery({
    queryKey: ['contact-groups'],
    queryFn: () => api.get<{ groups: ContactGroupMeta[] }>('/contact-groups').then((r) => r.groups),
    enabled,
  });
}

/** 单个分组 + 其联系人 */
export function useContactGroup(gid: string | undefined) {
  return useQuery({
    queryKey: ['contact-group', gid],
    queryFn: () =>
      api.get<{ group: ContactGroupMeta; contacts: GroupContact[] }>(`/contact-groups/${gid}`),
    enabled: !!gid,
  });
}

export function useCreateContactGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContactGroupInput) =>
      api.post<{ id: string }>('/contact-groups', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-groups'] }),
  });
}

export function useUpdateContactGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { gid: string; body: UpdateContactGroupInput }) =>
      api.patch<void>(`/contact-groups/${vars.gid}`, vars.body),
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ['contact-groups'] });
      qc.invalidateQueries({ queryKey: ['contact-group', vars.gid] });
    },
  });
}

export function useDeleteContactGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (gid: string) => api.del<{ ok?: boolean }>(`/contact-groups/${gid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-groups'] }),
  });
}

export function useAddGroupContact(gid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGroupContactInput) =>
      api.post<{ id: string; synced: number }>(`/contact-groups/${gid}/contacts`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-group', gid] });
      qc.invalidateQueries({ queryKey: ['contact-groups'] });
    },
  });
}

export function useUpdateGroupContact(gid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { cid: string; body: UpdateGroupContactInput }) =>
      api.patch<{ synced: number }>(`/contact-groups/${gid}/contacts/${vars.cid}`, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-group', gid] }),
  });
}

export function useDeleteGroupContact(gid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { cid: string; sync: boolean }) =>
      api.del<{ synced: number }>(`/contact-groups/${gid}/contacts/${vars.cid}`, {
        query: { sync: vars.sync ? 'true' : 'false' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact-group', gid] });
      qc.invalidateQueries({ queryKey: ['contact-groups'] });
    },
  });
}

/** 产品侧：从分组导入联系人 */
export function useImportGroup(tid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) =>
      api.post<{ imported: number; skipped: number }>(`/tenants/${tid}/contacts/import-group`, {
        group_id: groupId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts', tid] }),
  });
}
